import React, { useState, useEffect } from 'react';
import { categoryApi, formatApiError } from '../services/api';
import { Tag, Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  description: '',
  daily_rate: '',
  weekend_rate: '',
  weekly_rate: '',
  monthly_rate: '',
  default_deposit: '',
  insurance_deductible: ''
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await categoryApi.getAll();
      setCategories(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const numOrNull = (v) => (v === '' || v === null ? null : parseFloat(v));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        daily_rate: parseFloat(formData.daily_rate),
        weekend_rate: numOrNull(formData.weekend_rate),
        weekly_rate: numOrNull(formData.weekly_rate),
        monthly_rate: numOrNull(formData.monthly_rate),
        default_deposit: numOrNull(formData.default_deposit),
        insurance_deductible: numOrNull(formData.insurance_deductible)
      };
      if (editing) {
        await categoryApi.update(editing.id, payload);
        toast.success('Kategorie aktualisiert');
      } else {
        await categoryApi.create(payload);
        toast.success('Kategorie angelegt');
      }
      setShowModal(false);
      setEditing(null);
      setFormData(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleEdit = (cat) => {
    setEditing(cat);
    setFormData({
      name: cat.name || '',
      description: cat.description || '',
      daily_rate: cat.daily_rate ?? '',
      weekend_rate: cat.weekend_rate ?? '',
      weekly_rate: cat.weekly_rate ?? '',
      monthly_rate: cat.monthly_rate ?? '',
      default_deposit: cat.default_deposit ?? '',
      insurance_deductible: cat.insurance_deductible ?? ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    try {
      await categoryApi.delete(id);
      toast.success('Kategorie gelöscht');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const fmt = (v) => v == null ? '—' : `€ ${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="categories-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <p className="overline text-gray-500 mb-2">Verwaltung</p>
          <h1 className="h1 text-[#1a1a1a]">Fahrzeugkategorien</h1>
          <p className="body text-gray-600 mt-1">Tarife je Fahrzeugklasse — Tages-, Wochenend-, Wochen- und Monatspreise (alle brutto)</p>
        </div>
        <Button className="btn-primary" onClick={openNew} data-testid="add-category-btn">
          <Plus size={18} className="mr-2" />
          Neue Kategorie
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Lade Kategorien…</div>
      ) : categories.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <Tag size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="h3 mb-2">Noch keine Kategorien</h3>
          <p className="body text-gray-600 mb-6">
            Legen Sie Fahrzeugkategorien (z. B. Kompakt, Mittelklasse, Luxus-SUV) mit Standardpreisen an. Neue Fahrzeuge können die Preise dann automatisch übernehmen.
          </p>
          <Button className="btn-primary" onClick={openNew}>
            <Plus size={18} className="mr-2" />
            Erste Kategorie anlegen
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full card-swiss">
            <thead>
              <tr className="border-b border-[#E2E4E9] bg-[#F7F8FA]">
                <th className="text-left p-4 overline text-gray-600">Kategorie</th>
                <th className="text-right p-4 overline text-gray-600">Tag</th>
                <th className="text-right p-4 overline text-gray-600">Wochenende</th>
                <th className="text-right p-4 overline text-gray-600">Woche</th>
                <th className="text-right p-4 overline text-gray-600">Monat</th>
                <th className="text-right p-4 overline text-gray-600">Kaution / SB</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => (
                <tr key={cat.id} className="border-b border-[#E2E4E9] hover:bg-[#F7F8FA]" data-testid={`category-row-${cat.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-sm bg-red-50 flex items-center justify-center">
                        <Tag size={18} weight="duotone" className="text-[#E60000]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1a1a1a]">{cat.name}</p>
                        {cat.description && <p className="text-xs text-gray-500">{cat.description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-right font-semibold text-[#1a1a1a]">{fmt(cat.daily_rate)}</td>
                  <td className="p-4 text-right">{fmt(cat.weekend_rate)}</td>
                  <td className="p-4 text-right">{fmt(cat.weekly_rate)}</td>
                  <td className="p-4 text-right">{fmt(cat.monthly_rate)}</td>
                  <td className="p-4 text-right text-xs">
                    <div>{fmt(cat.default_deposit)}</div>
                    <div className="text-gray-500">{fmt(cat.insurance_deductible)}</div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => handleEdit(cat)} className="p-1.5 rounded-sm hover:bg-gray-100 text-gray-600" data-testid={`edit-category-${cat.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => handleDelete(cat.id)} className="p-1.5 rounded-sm hover:bg-red-50 text-red-600" data-testid={`delete-category-${cat.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="overline">Name *</Label>
              <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="input-swiss" required placeholder="z. B. Luxus-SUV" data-testid="category-name-input" />
            </div>
            <div>
              <Label className="overline">Beschreibung</Label>
              <Input value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="input-swiss" placeholder="Kurze Beschreibung" />
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">Tarife (brutto, inkl. MwSt.)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Tagespreis (€) *</Label>
                  <Input type="number" min={0} step={0.01} value={formData.daily_rate} onChange={e => setFormData({...formData, daily_rate: e.target.value})} className="input-swiss" required placeholder="z. B. 279" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Wochenendpreis (€)</Label>
                  <Input type="number" min={0} step={0.01} value={formData.weekend_rate} onChange={e => setFormData({...formData, weekend_rate: e.target.value})} className="input-swiss" placeholder="Pauschale Fr-So" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Wochenpreis (€)</Label>
                  <Input type="number" min={0} step={0.01} value={formData.weekly_rate} onChange={e => setFormData({...formData, weekly_rate: e.target.value})} className="input-swiss" placeholder="7-Tage Pauschale" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Monatspreis (€)</Label>
                  <Input type="number" min={0} step={0.01} value={formData.monthly_rate} onChange={e => setFormData({...formData, monthly_rate: e.target.value})} className="input-swiss" placeholder="28-Tage Pauschale" />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#E2E4E9]">
              <Label className="overline block mb-3">Standardwerte für neue Fahrzeuge</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Kaution (€)</Label>
                  <Input type="number" min={0} step={0.01} value={formData.default_deposit} onChange={e => setFormData({...formData, default_deposit: e.target.value})} className="input-swiss" placeholder="z. B. 2000" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Selbstbehalt Vollkasko (€)</Label>
                  <Input type="number" min={0} step={0.01} value={formData.insurance_deductible} onChange={e => setFormData({...formData, insurance_deductible: e.target.value})} className="input-swiss" placeholder="z. B. 2500" />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Abbrechen</Button>
              <Button type="submit" className="btn-primary" data-testid="save-category-btn">
                {editing ? 'Speichern' : 'Anlegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Categories;
